import React, { useEffect } from 'react';

import { api } from '../lib/api';
import { type AuthUser, useAuthStore } from '../stores/auth.store';

// Re-export store and types for convenience
export { useAuthStore };
export type { AuthUser };

/**
 * Hook for accessing auth state and actions.
 */
export function useAuth() {
  const store = useAuthStore();
  return {
    user: store.user,
    token: store.token,
    isAuthenticated: store.isAuthenticated,
    isGuest: store.isGuest,
    isLoading: store.isLoading,
    login: store.login,
    logout: store.logout,
    setGuestMode: store.setGuestMode,
  };
}

/**
 * AuthProvider validates the stored token on mount by calling /users/me.
 * If the token is invalid or expired, it clears auth state.
 * Guest mode: allows agent conversations without auth (no session persistence).
 */
export function AuthProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { token, setUser, logout, setLoading } = useAuthStore();

  useEffect(() => {
    async function validateToken() {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const rawUser = await api.get<any>('/users/me');
        const user: AuthUser = {
          id: rawUser.id,
          email: rawUser.email,
          display_name: rawUser.displayName ?? rawUser.display_name ?? null,
          photo_url: rawUser.photoUrl ?? rawUser.photo_url ?? null,
          is_admin: rawUser.isAdmin ?? rawUser.is_admin ?? false,
          locale: rawUser.locale,
          user_tier: rawUser.userTier ?? rawUser.user_tier ?? 'member',
        };
        setUser(user);
      } catch {
        logout();
      } finally {
        setLoading(false);
      }
    }

    validateToken();
  }, [token, setUser, logout, setLoading]);

  return <>{children}</>;
}
